import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Lock, ShieldCheck, FileDown, Loader2 } from "lucide-react";

/**
 * SideB Agent Agreement — two-stage direct-link signing flow.
 *
 * Mirrors the NDA-gated Quote pattern:
 *   1. Land on page via emailed URL (must include ?t=<token>)
 *   2. Accept the mutual NDA (name, email, checkbox) → unlocks commercial terms
 *   3. Review commissions/payouts + full T&C → sign the full Agreement
 *   4. Download the signed PDF
 *
 * Never indexed (noindex + X-Robots-Tag). Backend gates every request on the
 * magic token — a bare /agent-agreement/:id URL 404s.
 */
export default function AgentAgreement() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null); // 'not_found' | 'expired' | null

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setErrorState("not_found");
      setLoading(false);
      return;
    }
    api
      .get(`/agent-agreements/${id}`, { params: { t: token } })
      .then((r) => !cancelled && setAgreement(r.data))
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 410) setErrorState("expired");
        else setErrorState("not_found");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id, token]);

  if (loading) {
    return (
      <div className="bg-[#0a1628] text-white min-h-screen flex items-center justify-center" data-testid="agent-agreement-loading">
        <span className="text-sm text-white/45 font-mono uppercase tracking-[0.14em]">Loading agreement…</span>
      </div>
    );
  }
  if (errorState || !agreement) {
    return <ErrorState kind={errorState || "not_found"} />;
  }

  return (
    <div className="bg-white min-h-screen" data-testid="agent-agreement-page">
      <Helmet>
        <title>{`SideB Agent Agreement · ${agreement.agent_name}`}</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
      </Helmet>

      <Hero agreement={agreement} />

      {!agreement.nda_accepted ? (
        // Stage 1 — NDA gate. Commercial terms and full T&C stay locked.
        <NdaGate id={id} token={token} agreement={agreement} onAccepted={setAgreement} />
      ) : agreement.signed ? (
        // Stage 3 — signed. Show summary + PDF download.
        <SignedPanel id={id} token={token} agreement={agreement} />
      ) : (
        // Stage 2 — NDA is accepted; commercial terms unlocked. Show them
        // + full T&C, then final signature form.
        <>
          <CommercialTerms agreement={agreement} />
          <TermsBlock agreement={agreement} />
          <SignatureGate id={id} token={token} agreement={agreement} onSigned={setAgreement} />
        </>
      )}
    </div>
  );
}

/* --------------------------------- ERROR --------------------------------- */

function ErrorState({ kind }) {
  const isExpired = kind === "expired";
  return (
    <div className="bg-white min-h-screen flex items-center justify-center" data-testid={`agent-agreement-${isExpired ? "expired" : "not-found"}`}>
      <Helmet>
        <title>{isExpired ? "Agreement expired · SideB" : "Agreement not found · SideB"}</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      <div className="max-w-lg text-center px-6">
        <div className="label-mono text-foreground/55 mb-3">Agent Agreement</div>
        <h1 className="font-display text-2xl font-semibold tracking-[-0.02em]">
          {isExpired ? "This agreement link has expired." : "This agreement link is invalid or has expired."}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {isExpired
            ? "Agent Agreement links are valid for 14 days from creation. Ask your SideB contact for a fresh link."
            : (<>If you believe you should have access, contact <a href="mailto:swiles@sideb.io" className="text-[#ac62ea] underline">swiles@sideb.io</a>.</>)
          }
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------- HERO --------------------------------- */

function Hero({ agreement }) {
  return (
    <section className="bg-[#0a1628] text-white border-b border-white/10">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
        {agreement.preview_mode && (
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-amber-200"
            data-testid="agent-agreement-preview-banner"
          >
            <Lock className="h-3 w-3" />
            Admin preview · {agreement.signed_at ? "already signed by agent" : agreement.email_sent_at ? "already sent to agent" : "not yet sent to agent"}
          </div>
        )}
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#ac62ea] mb-4">
          <Lock className="w-3.5 h-3.5" />
          Confidential — unique to you
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-semibold tracking-[-0.03em] leading-[1.08]">
          SideB Agent Agreement
        </h1>
        <p className="mt-4 text-[15px] text-white/70 leading-relaxed max-w-2xl">
          Between <strong className="text-white">SideB Studio LLC</strong> (&ldquo;SideB&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;)
          and you as the individual or entity named in the commercial terms (&ldquo;Agent&rdquo;, &ldquo;you&rdquo;, &ldquo;your&rdquo;).
        </p>
        <div className="mt-5 flex items-center gap-3 flex-wrap text-xs text-white/60">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 font-mono uppercase tracking-[0.12em]">
            Link expires {agreement.expires_at}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 font-mono uppercase tracking-[0.12em]">
            Terms v{agreement.terms_version}
          </span>
          {agreement.signed ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 px-3 py-1 font-mono uppercase tracking-[0.12em]" data-testid="agent-agreement-signed-pill">
              <Check className="w-3 h-3" /> Signed {new Date(agreement.signed_at).toLocaleDateString()}
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- STAGE 1 -------------------------------- */

function NdaGate({ id, token, agreement, onAccepted }) {
  const [name, setName] = useState(agreement.agent_name || "");
  const [email, setEmail] = useState("");
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim() && email.includes("@") && ack && !submitting;
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/agent-agreements/${id}/accept-nda`, { name, email, acknowledged_nda: true }, { params: { t: token } });
      onAccepted(data);
      toast.success("NDA accepted. Commercial terms and full agreement are now visible below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record NDA acceptance.");
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="agent-agreement-nda-gate">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-xs font-mono uppercase tracking-[0.14em] text-foreground/55 mb-3">Step 1 of 2 · Mutual NDA</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] mb-3">
          Accept the mutual NDA to unlock the agreement.
        </h2>
        <p className="text-sm text-foreground/70 leading-relaxed max-w-2xl">
          {agreement.preamble}
        </p>

        <div className="mt-8 rounded-2xl border border-border bg-[#faf7ff] p-6 sm:p-7 max-w-3xl" data-testid="agent-agreement-nda-clause">
          <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#ac62ea] mb-2">Section 5 · Confidentiality &amp; NDA scope</div>
          <p className="text-sm text-foreground leading-relaxed">
            {(agreement.terms || []).find((t) => t.heading.startsWith("5."))?.body || ""}
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5 max-w-3xl" data-testid="agent-agreement-nda-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="grid gap-2 text-sm">
              <span className="text-xs font-medium text-foreground/70 uppercase tracking-[0.12em]">Your full legal name</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                className="rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
                data-testid="agent-agreement-nda-name" />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-xs font-medium text-foreground/70 uppercase tracking-[0.12em]">Your email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
                data-testid="agent-agreement-nda-email" />
            </label>
          </div>

          <label className="flex items-start gap-3 text-sm cursor-pointer pt-1">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#ac62ea]" data-testid="agent-agreement-nda-ack" />
            <span className="text-foreground/85 leading-relaxed">
              I acknowledge I may be exposed to <strong>Confidential Information</strong> about SideB
              Studio LLC, its clients, and its vendors, and I agree to be bound by the <strong>mutual
              non-disclosure obligation</strong> in Section 5 as described above.
            </span>
          </label>

          <div className="pt-2 flex items-center gap-4 flex-wrap">
            <Button type="submit" disabled={!canSubmit}
              className="rounded-full bg-[#b967ff] hover:bg-[#a855f7] h-11 px-6 text-sm font-medium disabled:opacity-50"
              data-testid="agent-agreement-nda-submit">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Accepting…</> : <>Accept NDA and continue</>}
            </Button>
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> Timestamp, IP, and user-agent are logged.
            </span>
          </div>
        </form>
      </div>
    </section>
  );
}

/* -------------------------- STAGE 2 · COMMERCIAL -------------------------- */

function CommercialTerms({ agreement }) {
  return (
    <section className="border-b border-border">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs font-mono uppercase tracking-[0.14em] text-emerald-700 mb-3">
          NDA accepted · Commercial terms unlocked
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] mb-6">Commercial terms</h2>
        <div className="grid sm:grid-cols-2 gap-6" data-testid="agent-agreement-commercial-terms">
          <TermCard label="Agent" value={agreement.agent_name + (agreement.agent_org ? ` · ${agreement.agent_org}` : "")} full />
          <TermCard label="Commission" value={agreement.commission || "—"} full />
          <TermCard label="Payout schedule" value={agreement.payout_schedule || "—"} />
          <TermCard label="Services covered" value={(agreement.services_covered || []).join(", ") || "—"} />
          {agreement.notes ? <TermCard label="Additional notes" value={agreement.notes} full /> : null}
        </div>
      </div>
    </section>
  );
}

const TermCard = ({ label, value, full = false }) => (
  <div className={`rounded-xl border border-border bg-white p-5 ${full ? "sm:col-span-2" : ""}`}>
    <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-foreground/55 mb-2">{label}</div>
    <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{value}</div>
  </div>
);

/* -------------------------- STAGE 2 · FULL T&C --------------------------- */

function TermsBlock({ agreement }) {
  return (
    <section className="border-b border-border bg-[#faf7ff]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12" data-testid="agent-agreement-terms">
        <div className="label-mono mb-3">Full terms &amp; conditions</div>
        <p className="text-xs text-muted-foreground mb-6">
          Version {agreement.terms_version} · Governing law: California, USA · Venue: Orange County, California · Disputes: binding JAMS arbitration
        </p>
        <ol className="space-y-6">
          {(agreement.terms || []).map((t, idx) => (
            <li key={`term-${idx}`} data-testid={`agent-agreement-term-${idx + 1}`}>
              <h3 className="font-display text-[17px] font-semibold text-foreground mb-2 tracking-[-0.01em]">{t.heading}</h3>
              <p className="text-sm text-foreground/80 leading-relaxed">{t.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------------------- STAGE 2 · SIGN THE AGREEMENT --------------------- */

function SignatureGate({ id, token, agreement, onSigned }) {
  // Pre-populate name/email from NDA acceptance (returned on the accept-nda
  // response), fallback to agent_name on file.
  const [name, setName] = useState(agreement.signed_by_name || agreement.agent_name || "");
  const [email, setEmail] = useState(agreement.signed_by_email || "");
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim() && email.includes("@") && ack && !submitting;
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/agent-agreements/${id}/sign`, { name, email, acknowledged_terms: true }, { params: { t: token } });
      onSigned(data);
      toast.success("Agreement signed. A copy has been sent to SideB.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record signature.");
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="agent-agreement-signature-gate">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="rounded-2xl border border-border bg-white p-8 lg:p-10 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.14em] text-foreground/55 mb-3">Step 2 of 2 · Sign the Agreement</div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Sign electronically to activate.
          </h2>
          <p className="text-foreground/70 mt-3 leading-relaxed text-sm">
            Your electronic signature is legally binding under the Uniform Electronic
            Transactions Act and the ESIGN Act. Signing binds both parties to every
            section above, including the commercial terms.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="agent-agreement-form">
            <label className="grid gap-2 text-sm">
              <span className="text-xs font-medium text-foreground/70 uppercase tracking-[0.12em]">Your full legal name</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                className="rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
                data-testid="agent-agreement-name" />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-xs font-medium text-foreground/70 uppercase tracking-[0.12em]">Your email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
                data-testid="agent-agreement-email" />
            </label>

            <label className="flex items-start gap-3 text-sm cursor-pointer pt-1">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#ac62ea]" data-testid="agent-agreement-ack-terms" />
              <span className="text-foreground/85 leading-relaxed">
                I have read and agree to the full <strong>Terms &amp; Conditions</strong> shown above —
                including the commercial terms, the no-circumvention clause, the limitation of
                liability, and the binding-arbitration clause with venue in Orange County, California.
              </span>
            </label>

            <div className="pt-4 flex items-center gap-4 flex-wrap">
              <Button type="submit" disabled={!canSubmit}
                className="rounded-full bg-[#b967ff] hover:bg-[#a855f7] h-11 px-6 text-sm font-medium disabled:opacity-50"
                data-testid="agent-agreement-sign-submit">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing…</> : <>Sign the Agent Agreement</>}
              </Button>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Timestamp, IP, and user-agent are logged.
              </span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- STAGE 3 · SIGNED ---------------------------- */

function SignedPanel({ id, token, agreement }) {
  return (
    <section data-testid="agent-agreement-signed-panel">
      <CommercialTerms agreement={agreement} />
      <TermsBlock agreement={agreement} />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-50/60 p-8 lg:p-10 max-w-3xl">
          <div className="flex items-center gap-3 mb-3">
            <Check className="h-5 w-5 text-emerald-600" />
            <div className="label-mono text-emerald-800">Signed and active</div>
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">
            Your Agent Agreement is signed.
          </h2>
          <p className="mt-3 text-sm text-foreground/70 leading-relaxed">
            SideB has been notified. Download a copy for your records. Any commissions
            earned under this agreement will be paid on the schedule shown above.
          </p>
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900 leading-relaxed" data-testid="agent-agreement-submit-reminder">
            <strong>Submitting deals for commission credit:</strong>{" "}
            All prospective client introductions must be submitted for review and approval
            at <a href="https://sideb.io/agent" className="underline font-medium">https://sideb.io/agent</a>. We&apos;ll
            respond within 72 hours. Introductions made through any other channel aren&apos;t
            eligible for commission unless we confirm acceptance in writing (see §2).
          </div>
          <div className="mt-6">
            <a
              href={`${API}/agent-agreements/${id}/download.pdf?t=${encodeURIComponent(token)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#0b1e5a] hover:bg-black text-white px-5 py-2.5 text-sm font-medium transition-colors"
              data-testid="agent-agreement-download-pdf"
            >
              <FileDown className="h-4 w-4" />
              Download the signed agreement (PDF)
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
